"use client";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useTranslations } from "next-intl";
import { useState } from "react";

interface ClaimClubDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  clubId: string;
  clubName: string;
}

type FormStatus = "idle" | "submitting" | "success" | "error";

export function ClaimClubDialog({
  open,
  onOpenChange,
  clubId,
  clubName,
}: ClaimClubDialogProps) {
  const t = useTranslations();
  const basePath = process.env.NEXT_PUBLIC_BASE_PATH ?? "";
  const [status, setStatus] = useState<FormStatus>("idle");
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    role: "",
    message: "",
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    setStatus("submitting");
    setErrorMessage("");

    try {
      const payload = {
        ...formData,
        clubId,
        clubName,
      };

      const response = await fetch(`${basePath}/api/claim-club`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (response.ok) {
        setStatus("success");
      } else {
        setStatus("error");
        setErrorMessage(data.error || t("claimClub.error"));
      }
    } catch {
      setStatus("error");
      setErrorMessage(t("claimClub.error"));
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleClose = () => {
    onOpenChange(false);
    setTimeout(() => {
      setStatus("idle");
      setErrorMessage("");
      setFormData({ name: "", email: "", phone: "", role: "", message: "" });
    }, 200);
  };

  if (status === "success") {
    return (
      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent className="sm:max-w-md">
          <div className="py-6 text-center">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
              <svg
                className="h-6 w-6 text-green-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
            <DialogHeader>
              <DialogTitle className="text-center">
                {t("claimClub.successTitle")}
              </DialogTitle>
              <DialogDescription className="text-center mt-2">
                {t("claimClub.successMessage", {
                  clubName,
                  email: formData.email,
                })}
              </DialogDescription>
            </DialogHeader>
            <p className="mt-4 text-sm text-muted-foreground">
              {t("claimClub.contactInfo")}{" "}
              <a
                href="https://www.spond.com/contact/"
                target="_blank"
                rel="noopener noreferrer"
                className="underline hover:text-foreground"
              >
                {t("claimClub.contactLink")}
              </a>
            </p>
            <Button onClick={handleClose} className="mt-6">
              {t("claimClub.done")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t("claimClub.title")}</DialogTitle>
          <DialogDescription>
            {t("claimClub.subtitle", { clubName })}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="claim-name">{t("claimClub.name")}</Label>
            <Input
              id="claim-name"
              name="name"
              placeholder={t("claimClub.namePlaceholder")}
              value={formData.name}
              onChange={handleChange}
              maxLength={100}
              required
              disabled={status === "submitting"}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="claim-email">{t("claimClub.email")}</Label>
            <Input
              id="claim-email"
              name="email"
              type="email"
              placeholder={t("claimClub.emailPlaceholder")}
              value={formData.email}
              onChange={handleChange}
              maxLength={255}
              required
              disabled={status === "submitting"}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="claim-phone">{t("claimClub.phone")}</Label>
            <Input
              id="claim-phone"
              name="phone"
              type="tel"
              placeholder={t("claimClub.optional")}
              value={formData.phone}
              onChange={handleChange}
              maxLength={50}
              disabled={status === "submitting"}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="claim-role">{t("claimClub.role")}</Label>
            <Input
              id="claim-role"
              name="role"
              placeholder={t("claimClub.rolePlaceholder")}
              value={formData.role}
              onChange={handleChange}
              maxLength={100}
              disabled={status === "submitting"}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="claim-message">{t("claimClub.message")}</Label>
            <Textarea
              id="claim-message"
              name="message"
              placeholder={t("claimClub.messagePlaceholder")}
              value={formData.message}
              onChange={handleChange}
              maxLength={2000}
              rows={3}
              disabled={status === "submitting"}
            />
          </div>

          {errorMessage && (
            <p className="text-sm text-destructive">{errorMessage}</p>
          )}

          <DialogFooter className="gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={status === "submitting"}
            >
              {t("claimClub.cancel")}
            </Button>
            <Button type="submit" disabled={status === "submitting"}>
              {status === "submitting"
                ? t("claimClub.sending")
                : t("claimClub.submit")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
